import Image from "next/image";
import { formatToLakhs, formatCurrencyINR } from "@utils/numberFormatter";
import { SHORTLIST_LABELS } from "@constants/shortlist";
import assetLoader from "@utils/internalImageLoder";
import { shortlistCardData } from "../ShortlistCard.data";
import styles from "./VariantOptionCard.module.scss";

const { iconPaths, iconSizes } = shortlistCardData;

export interface VariantOptionCardProps {
  variantName: string;
  fuelType: string;
  transmission: string;
  mileage?: { value: string; unit: string };
  isSellable: boolean;
  isDisabled?: boolean;
  adspPrice: number;
  marketPrice?: number;
  savings?: number;
  fallbackPrice?: number;
  isExpressDelivery?: boolean;
  deliveryDate?: string;
  isSelected?: boolean;
  isCompareMode?: boolean;
  isCompareSelected?: boolean;
  onClick?: () => void;
  className?: string;
}

const VariantOptionCard: React.FC<VariantOptionCardProps> = ({
  variantName,
  fuelType,
  transmission,
  mileage,
  isSellable,
  isDisabled = false,
  adspPrice,
  marketPrice = 0,
  savings = 0,
  fallbackPrice = 0,
  isExpressDelivery = false,
  deliveryDate,
  isSelected = false,
  isCompareMode = false,
  isCompareSelected = false,
  onClick,
  className,
}) => {
  const assetBase = assetLoader();
  const showStrikethrough =
    isSellable && marketPrice > 0 && marketPrice !== adspPrice;

  const cardClasses = [
    styles.card,
    !isSellable ? styles.unavailable : "",
    isSelected && !isCompareMode ? styles.selected : "",
    isCompareSelected ? styles.compareSelected : "",
    className || "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={cardClasses}
      onClick={() => {
        if (!isDisabled) onClick?.();
      }}
      aria-disabled={isDisabled}
      tabIndex={isDisabled ? -1 : undefined}
    >
      <div className={styles.topRow}>
        <div className={styles.info}>
          <span className={styles.name}>{variantName}</span>
          <span className={styles.specs}>
            {fuelType} &bull; {transmission}
            {mileage?.value && ` • ${mileage.value} ${mileage.unit}`}
          </span>
        </div>
        <div className={styles.pricing}>
          {isSellable ? (
            <>
              <div className={styles.priceRow}>
                {showStrikethrough && (
                  <span className={styles.marketPrice}>
                    ₹{formatToLakhs(marketPrice)} L
                  </span>
                )}
                <span className={styles.adspPrice}>
                  ₹{formatToLakhs(adspPrice)} L
                </span>
              </div>
              {savings > 0 && (
                <span className={styles.savings}>
                  {SHORTLIST_LABELS.SAVE_PREFIX} {formatCurrencyINR(savings)}
                </span>
              )}
            </>
          ) : (
            <span className={styles.unavailablePrice}>
              ₹{formatToLakhs(fallbackPrice || marketPrice)} L
            </span>
          )}
        </div>
      </div>

      <div className={styles.bottomRow}>
        <div className={styles.delivery}>
          {isSellable && deliveryDate ? (
            <>
              <span
                className={
                  isExpressDelivery
                    ? styles.expressLabel
                    : styles.standardLabel
                }
              >
                {isExpressDelivery
                  ? `${SHORTLIST_LABELS.EXPRESS_DELIVERY_BY} ${deliveryDate}`
                  : `${SHORTLIST_LABELS.DELIVERY_BY} ${deliveryDate}`}
              </span>
              <Image
                src={`${assetBase}${
                  isExpressDelivery
                    ? iconPaths.expressDelivery
                    : iconPaths.standardDelivery
                }`}
                alt=""
                width={iconSizes.small.width}
                height={iconSizes.small.height}
              />
            </>
          ) : !isSellable ? (
            <>
              <Image
                src={`${assetBase}${iconPaths.exclamation}`}
                alt=""
                width={iconSizes.small.width}
                height={iconSizes.small.height}
              />
              <span className={styles.unavailableLabel}>
                {SHORTLIST_LABELS.UNAVAILABLE}
              </span>
            </>
          ) : null}
        </div>
        {!isCompareMode && (
          <Image
            src={`${assetBase}${iconPaths.arrowRight}`}
            alt=""
            width={iconSizes.medium.width}
            height={iconSizes.medium.height}
            className={styles.arrow}
          />
        )}
      </div>
    </div>
  );
};

export default VariantOptionCard;
